import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Clock, AlertTriangle, X } from "lucide-react";
import { usePlan } from "../../lib/usePlan";

const DISMISS_KEY = "renewal_banner_dismissed_at";
const REDISMISS_HOURS = 12;

/**
 * Global thin banner shown at the top of the app when the subscription
 * is within 7 days of expiry or has already expired.
 * Dismissable for 12h via localStorage so it doesn't nag the user every nav.
 */
export default function RenewalBanner() {
  const plan = usePlan();
  const [dismissed, setDismissed] = useState(false);

  // Read dismiss state once on mount
  useEffect(() => {
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (!ts) return;
      const age = Date.now() - Number(ts);
      if (age < REDISMISS_HOURS * 3600 * 1000) setDismissed(true);
    } catch {
      /* ignore */
    }
  }, []);

  if (plan?.loading) return null;
  if (plan?.adminBypass) return null;

  const status = plan?.status;
  const daysLeft = plan?.daysUntilExpiry;

  // Expired: never dismissable, top priority
  const isExpired = status === "expired" || status === "past_due";
  // Expiring within 7 days
  const isExpiringSoon =
    !isExpired &&
    ["active", "trialing"].includes(status) &&
    typeof daysLeft === "number" &&
    daysLeft <= 7;

  if (!isExpired && !isExpiringSoon) return null;
  if (dismissed && !isExpired) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  let bg = "bg-amber-50 border-amber-200 text-amber-900";
  let Icon = Clock;
  let label = "";
  if (isExpired) {
    bg = "bg-red-50 border-red-200 text-red-900";
    Icon = AlertTriangle;
    label = "Il tuo piano è scaduto. Rinnova ora per riattivare le funzionalità Pro/Club.";
  } else if (daysLeft <= 1) {
    bg = "bg-red-50 border-red-200 text-red-900";
    Icon = AlertTriangle;
    label = daysLeft === 0
      ? "Il piano scade oggi: rinnova ora per non perdere l'accesso."
      : "Il piano scade domani: rinnova ora per non perdere l'accesso.";
  } else if (daysLeft <= 3) {
    label = `Il piano scade tra ${daysLeft} giorni. Rinnova ora per evitare interruzioni.`;
  } else {
    label = `Il piano scade tra ${daysLeft} giorni.`;
  }

  return (
    <div
      className={`border-b ${bg} px-4 py-2.5 flex items-center gap-3`}
      data-testid="renewal-banner"
    >
      <Icon size={16} className="shrink-0" />
      <div className="text-sm flex-1 truncate">{label}</div>
      <Link
        to="/app/billing"
        className="text-sm font-semibold underline-offset-4 hover:underline shrink-0"
        data-testid="renewal-banner-cta"
      >
        Rinnova ora →
      </Link>
      {!isExpired && (
        <button
          onClick={handleDismiss}
          className="shrink-0 hover:bg-black/5 rounded p-1"
          data-testid="renewal-banner-dismiss"
          aria-label="Chiudi avviso rinnovo"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
